import type { AnchorHTMLAttributes } from 'react';
import { useId } from 'react';

import { cn } from '#/utils/cn';

import type { IconId } from './icons';
import { Icon } from './icons';
import { resolveLinkTarget } from './linkTarget';
import type { HeadingLevel } from './SectionHeader';

const CONTENT_LINE_CLAMP_CLASS = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
} as const;

interface ArticleCardProps extends Pick<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'target' | 'rel'
> {
  title: string;
  content?: string | null;
  icon?: IconId;
  href?: string;
  /** Heading element for the card title. Pick for the document outline in descending order (h2 -> h3, etc) */
  headingLevel?: HeadingLevel;
  contentLineClamp?: keyof typeof CONTENT_LINE_CLAMP_CLASS;
  className?: string;
  /** GTM `data-track` id, forwarded onto the "Learn More" anchor. */
  dataTrack?: string;
  /** GTM `data-track-label`, forwarded onto the "Learn More" anchor. */
  dataTrackLabel?: string;
  /** GTM `data-track-context`, forwarded onto the "Learn More" anchor. */
  dataTrackContext?: string;
}

export function ArticleCard({
  title,
  content,
  href,
  icon = 'newspaper',
  headingLevel: Heading = 'h2',
  contentLineClamp,
  className,
  target,
  rel,
  dataTrack,
  dataTrackLabel,
  dataTrackContext,
}: ArticleCardProps) {
  const titleId = useId();
  const linkId = useId();
  const linkProps = resolveLinkTarget({ href, target, rel });

  const cardClasses = cn(
    'group/article-card border-blue-20 bg-gray-10 flex h-full flex-col rounded border p-24 shadow-(--shadow-card)',
    href && 'relative',
    className,
  );

  const cardContent = (
    <>
      <div className="flex items-start justify-between gap-12">
        <div className="flow flow-12">
          <p className="text-static-12 text-fg-header leading-none font-semibold">
            Article
          </p>
          <Heading
            id={titleId}
            className="text-fg-header text-static-14 mb-12 leading-tight font-semibold"
          >
            {title}
          </Heading>
        </div>

        <div className="bg-blue-30 text-blue-60 flex size-32 shrink-0 items-center justify-center rounded-full">
          <Icon id={icon} size={18} aria-hidden="true" />
        </div>
      </div>

      {content && (
        <p
          className={cn(
            'text-static-12 text-fg leading-relaxed',
            contentLineClamp && CONTENT_LINE_CLAMP_CLASS[contentLineClamp],
          )}
        >
          {content}
        </p>
      )}

      {href ? (
        <a
          href={href}
          target={linkProps.target}
          rel={linkProps.rel}
          id={linkId}
          data-track={dataTrack}
          data-track-label={dataTrackLabel}
          data-track-context={dataTrackContext}
          aria-labelledby={`${linkId} ${titleId}`}
          className="text-static-14 tracking-link-caps mt-16 inline-flex items-center gap-12 self-start leading-none font-semibold uppercase no-underline hover:underline group-hover/article-card:underline"
        >
          <span>Learn More</span>
          <Icon
            id="arrow-right"
            size={18}
            aria-hidden="true"
            className="transition-transform group-hover/article-card:translate-x-4"
          />
          <span
            aria-hidden="true"
            className="after:absolute after:inset-0 after:block after:content-['']"
          />
        </a>
      ) : null}
    </>
  );

  return <article className={cardClasses}>{cardContent}</article>;
}
